import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminClientsAPI } from '../services/api';
import toast from 'react-hot-toast';
import './AdminClients.css';

const AdminClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activity, setActivity] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [ipWorking, setIpWorking] = useState(null);
  const [billPayments, setBillPayments] = useState([]);
  const [clientDepositMethods, setClientDepositMethods] = useState([]);

  // Client deposit method form
  const [showDepositMethodForm, setShowDepositMethodForm] = useState(false);
  const [editingDepositMethod, setEditingDepositMethod] = useState(null);
  const [depositMethodForm, setDepositMethodForm] = useState({
    method_name: '', method_type: 'wire_transfer', deposit_amount: '', instructions: '',
    recipient_name: '', recipient_address: '', bank_name: '', account_number: '', routing_number: '',
    payment_address: '', nearest_branch_map_link: '', additional_notes: '', is_active: true,
    crypto_type: 'btc', wallet_address: '', qr_image_url: '',
    pickup_carrier: 'fedex', pickup_location: '', pickup_scheduled_date: '', insured_value: ''
  });
  const [qrFile, setQrFile] = useState(null);
  const [branchPhotoFile, setBranchPhotoFile] = useState(null);

  // Pickup tracking form
  const [trackingMethod, setTrackingMethod] = useState(null);
  const [trackingForm, setTrackingForm] = useState({
    pickup_status: 'scheduled', picker_name: '', picker_image: '',
    car_name: '', car_number: '', estimated_arrival: ''
  });
  const [pickerFile, setPickerFile] = useState(null);

  // New transaction form
  const [showTxnForm, setShowTxnForm] = useState(false);
  const [txnForm, setTxnForm] = useState({ description: '', credit_amount: '', debit_amount: '', status: 'available', category: '', account_id: '' });

  // Client bank accounts
  const [clientAccounts, setClientAccounts] = useState([]);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [accountForm, setAccountForm] = useState({ account_type: 'checking', account_name: '', account_number: '', balance: '', status: 'active' });

  // New notification form
  const [showNotifForm, setShowNotifForm] = useState(false);
  const [notifForm, setNotifForm] = useState({ title: '', message: '', notification_type: 'notice', priority: 'normal', active: true });
  const [editingNotif, setEditingNotif] = useState(null);

  // Document upload
  const [docName, setDocName] = useState('');
  const [docFile, setDocFile] = useState(null);

  useEffect(() => { loadClient(); }, [id]);
  useEffect(() => { if (activeTab === 'transactions') { loadTransactions(); loadAccounts(); } }, [activeTab]);
  useEffect(() => { if (activeTab === 'accounts') loadAccounts(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'documents') loadDocuments(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'notifications') loadNotifications(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'activity') { loadActivity(); loadBlockedIps(); } }, [activeTab]);
  useEffect(() => { if (activeTab === 'billPayments') loadBillPayments(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'depositMethods') loadClientDepositMethods(); }, [activeTab]);

  const loadAccounts = async () => {
    try {
      const response = await adminClientsAPI.getAccounts(id);
      setClientAccounts(response.data);
    } catch (error) { toast.error('Failed to load accounts'); }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        account_type: accountForm.account_type,
        account_name: accountForm.account_name,
        account_number: accountForm.account_number,
        status: accountForm.status,
        balance: accountForm.balance === '' ? 0 : parseFloat(accountForm.balance)
      };
      if (editingAccountId) {
        await adminClientsAPI.updateAccount(id, editingAccountId, payload);
        toast.success('Account updated');
      } else {
        await adminClientsAPI.createAccount(id, payload);
        toast.success('Account created');
      }
      setShowAccountForm(false);
      setEditingAccountId(null);
      setAccountForm({ account_type: 'checking', account_name: '', account_number: '', balance: '', status: 'active' });
      loadAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save account');
    }
  };

  const handleEditAccount = (acct) => {
    setEditingAccountId(acct.id);
    setAccountForm({
      account_type: acct.account_type || 'checking',
      account_name: acct.account_name || '',
      account_number: acct.account_number || '',
      balance: acct.balance ?? '',
      status: acct.status || 'active'
    });
    setShowAccountForm(true);
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Delete this account? Any transactions posted to it will remain in history.')) return;
    try {
      await adminClientsAPI.deleteAccount(id, accountId);
      toast.success('Account deleted');
      loadAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete account');
    }
  };

  const loadClient = async () => {
    try {
      const response = await adminClientsAPI.getById(id);
      setClient(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Failed to load client');
      navigate('/admin/clients');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await adminClientsAPI.getTransactions(id);
      setTransactions(response.data);
    } catch (error) { toast.error('Failed to load transactions'); }
  };

  const loadDocuments = async () => {
    try {
      const response = await adminClientsAPI.getDocuments(id);
      setDocuments(response.data);
    } catch (error) { toast.error('Failed to load documents'); }
  };

  const loadNotifications = async () => {
    try {
      const response = await adminClientsAPI.getNotifications(id);
      setNotifications(response.data);
    } catch (error) { toast.error('Failed to load notifications'); }
  };

  const loadActivity = async () => {
    try {
      const response = await adminClientsAPI.getActivity(id);
      setActivity(response.data);
    } catch (error) { toast.error('Failed to load activity'); }
  };

  const loadBlockedIps = async () => {
    try {
      const response = await adminClientsAPI.getBlockedIps(id);
      setBlockedIps(response.data);
    } catch (error) { /* non-fatal */ }
  };

  const handleBlockIp = async (ip, reason) => {
    setIpWorking(ip);
    try {
      await adminClientsAPI.blockIp(id, { ip_address: ip, reason: reason || 'Blocked from client activity' });
      toast.success(`IP ${ip} blocked`);
      await Promise.all([loadBlockedIps(), loadActivity()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to block IP');
    }
    setIpWorking(null);
  };

  const handleUnblockIp = async (blockId, ip) => { // blockId may be a row id or the raw IP (unified endpoint accepts both)
    setIpWorking(ip);
    try {
      await adminClientsAPI.unblockIp(id, blockId);
      toast.success('IP unblocked');
      await Promise.all([loadBlockedIps(), loadActivity()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to unblock IP');
    }
    setIpWorking(null);
  };

  const loadBillPayments = async () => {
    try {
      const response = await adminClientsAPI.getBillPayments(id);
      setBillPayments(response.data);
    } catch (error) { toast.error('Failed to load bill payments'); }
  };

  const loadClientDepositMethods = async () => {
    try {
      const response = await adminClientsAPI.getDepositMethods(id);
      setClientDepositMethods(response.data);
    } catch (error) { toast.error('Failed to load deposit methods'); }
  };

  const handleCreateDepositMethod = async (e) => {
    e.preventDefault();
    try {
      let qrUrl = depositMethodForm.qr_image_url;
      // Upload QR code if a new file was selected
      if (qrFile) {
        const qrFormData = new FormData();
        qrFormData.append('logo', qrFile);
        const { uploadAPI } = require('../services/api');
        const qrRes = await uploadAPI.uploadLogo(qrFile);
        qrUrl = qrRes.data.logo_url;
      }
      const formDataWithQr = { ...depositMethodForm, qr_image_url: qrUrl || '' };
      if (editingDepositMethod) {
        await adminClientsAPI.updateDepositMethod(id, editingDepositMethod.id, formDataWithQr);
        toast.success('Deposit method updated');
      } else {
        await adminClientsAPI.createDepositMethod(id, formDataWithQr);
        toast.success('Deposit method created');
      }
      setShowDepositMethodForm(false);
      setEditingDepositMethod(null);
      setDepositMethodForm({ method_name: '', method_type: 'wire_transfer', deposit_amount: '', instructions: '', recipient_name: '', bank_name: '', account_number: '', routing_number: '', payment_address: '', nearest_branch_map_link: '', additional_notes: '', is_active: true, crypto_type: 'btc', wallet_address: '', qr_image_url: '', pickup_carrier: 'fedex', pickup_location: '', pickup_scheduled_date: '', insured_value: '' });
      setQrFile(null);
      loadClientDepositMethods();
    } catch (error) { toast.error(error.response?.data?.error || 'Failed to save deposit method'); }
  };

  const handleEditDepositMethod = (method) => {
    setEditingDepositMethod(method);
    setDepositMethodForm({
      method_name: method.method_name,
      method_type: method.method_type,
      deposit_amount: method.deposit_amount || '',
      instructions: method.instructions || '',
      recipient_name: method.recipient_name || '',
      recipient_address: method.recipient_address || '',
      bank_name: method.bank_name || '',
      account_number: method.account_number || '',
      routing_number: method.routing_number || '',
      payment_address: method.payment_address || '',
      nearest_branch_map_link: method.nearest_branch_map_link || '',
      additional_notes: method.additional_notes || '',
      is_active: method.is_active === 1,
      crypto_type: method.crypto_type || 'btc',
      wallet_address: method.wallet_address || '',
      qr_image_url: method.qr_image_url || '',
      pickup_carrier: method.pickup_carrier || 'fedex',
      pickup_location: method.pickup_location || '',
      pickup_scheduled_date: method.pickup_scheduled_date || '',
      insured_value: method.insured_value || ''
    });
    setQrFile(null);
    setShowDepositMethodForm(true);
  };

  const handleDeleteDepositMethod = async (methodId) => {
    if (!window.confirm('Delete this deposit method?')) return;
    try {
      await adminClientsAPI.deleteDepositMethod(id, methodId);
      toast.success('Deposit method deleted');
      loadClientDepositMethods();
    } catch (error) { toast.error('Failed to delete deposit method'); }
  };

  const handleOpenTracking = (method) => {
    setTrackingMethod(method);
    setTrackingForm({
      pickup_status: method.pickup_status || 'scheduled',
      picker_name: method.picker_name || '',
      picker_image: method.picker_image || '',
      car_name: method.car_name || '',
      car_number: method.car_number || '',
      estimated_arrival: method.estimated_arrival || ''
    });
    setPickerFile(null);
  };

  const handleUpdateTracking = async (e) => {
    e.preventDefault();
    try {
      let pickerImageUrl = trackingForm.picker_image;
      if (pickerFile) {
        const { uploadAPI } = require('../services/api');
        const pickerRes = await uploadAPI.uploadLogo(pickerFile);
        pickerImageUrl = pickerRes.data.logo_url;
      }
      const dataToSend = { ...trackingForm, picker_image: pickerImageUrl || '' };
      await adminClientsAPI.updatePickupTracking(id, trackingMethod.id, dataToSend);
      toast.success('Tracking updated successfully');
      setTrackingMethod(null);
      setTrackingForm({ pickup_status: 'scheduled', picker_name: '', picker_image: '', car_name: '', car_number: '', estimated_arrival: '' });
      setPickerFile(null);
      loadClientDepositMethods();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update tracking');
    }
  };

  const handleCancelDepositMethod = () => {
    setEditingDepositMethod(null);
    setDepositMethodForm({ method_name: '', method_type: 'wire_transfer', deposit_amount: '', instructions: '', recipient_name: '', recipient_address: '', bank_name: '', account_number: '', routing_number: '', payment_address: '', nearest_branch_map_link: '', additional_notes: '', is_active: true, crypto_type: 'btc', wallet_address: '', qr_image_url: '', pickup_carrier: 'fedex', pickup_location: '', pickup_scheduled_date: '', insured_value: '' });
    setQrFile(null);
    setShowDepositMethodForm(false);
  };

  const handleUpdateBillStatus = async (billId, newStatus) => {
    try {
      await adminClientsAPI.updateBillPaymentStatus(id, billId, { status: newStatus });
      toast.success(`Bill payment status updated to ${newStatus}`);
      loadBillPayments();
      loadClient();
      loadTransactions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update status');
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      // Upload the branch photo first, if a new one was selected
      let branchData = editData;
      if (branchPhotoFile) {
        const { uploadAPI } = require('../services/api');
        const photoRes = await uploadAPI.uploadLogo(branchPhotoFile);
        branchData = { ...editData, branch_photo_url: photoRes.data.logo_url };
        setBranchPhotoFile(null);
      }
      await adminClientsAPI.update(id, branchData);
      toast.success('Client updated');
      setEditMode(false);
      loadClient();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleRemoveBranchPhoto = async () => {
    try {
      await adminClientsAPI.update(id, { branch_photo_url: null });
      setBranchPhotoFile(null);
      toast.success('Branch photo removed');
      loadClient();
    } catch (error) {
      toast.error('Failed to remove branch photo');
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      await adminClientsAPI.createTransaction(id, txnForm);
      toast.success('Transaction created');
      setShowTxnForm(false);
      setTxnForm({ description: '', credit_amount: '', debit_amount: '', status: 'completed', category: '', account_id: '' });
      loadTransactions();
      loadAccounts();
      loadClient();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create transaction');
    }
  };

  const handleDeleteTransaction = async (txnId) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await adminClientsAPI.deleteTransaction(id, txnId);
      toast.success('Transaction deleted');
      loadTransactions();
      loadClient();
    } catch (error) { toast.error('Failed to delete transaction'); }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!docName) { toast.error('Document name is required'); return; }
    try {
      const formData = new FormData();
      formData.append('document_name', docName);
      formData.append('document_type', 'document');
      if (docFile) formData.append('file', docFile);
      await adminClientsAPI.uploadDocument(id, formData);
      toast.success('Document uploaded');
      setDocName('');
      setDocFile(null);
      loadDocuments();
    } catch (error) { toast.error('Failed to upload document'); }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await adminClientsAPI.deleteDocument(id, docId);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) { toast.error('Failed to delete document'); }
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    try {
      if (editingNotif) {
        await adminClientsAPI.updateNotification(id, editingNotif.id, notifForm);
        toast.success('Notification updated');
      } else {
        await adminClientsAPI.createNotification(id, notifForm);
        toast.success('Notification sent');
      }
      setShowNotifForm(false);
      setEditingNotif(null);
      setNotifForm({ title: '', message: '', notification_type: 'notice', priority: 'normal', active: true });
      loadNotifications();
    } catch (error) { toast.error('Failed to save notification'); }
  };

  const handleEditNotification = (notif) => {
    setEditingNotif(notif);
    setNotifForm({
      title: notif.title,
      message: notif.message,
      notification_type: notif.notification_type || 'notice',
      priority: notif.priority || 'normal',
      active: notif.active !== 0
    });
    setShowNotifForm(true);
  };

  const handleDeleteNotification = async (notifId) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      await adminClientsAPI.deleteNotification(id, notifId);
      toast.success('Notification deleted');
      loadNotifications();
    } catch (error) { toast.error('Failed to delete notification'); }
  };

  const handleCancelEditNotif = () => {
    setEditingNotif(null);
    setNotifForm({ title: '', message: '', notification_type: 'notice', priority: 'normal', active: true });
    setShowNotifForm(false);
  };

  // Single source of truth for "is this IP blocked?": derive the set from the
  // blocked-ips table. The activity rows also carry ip_blocked from the server,
  // but the derived set stays correct immediately after a block/unblock toggle.
  const blockedIpSet = new Set((blockedIps || []).map(b => b.ip_address));
  const isIpBlocked = (ip) => blockedIpSet.has(ip);
  const findBlockForIp = (ip) => (blockedIps || []).find(b => b.ip_address === ip);

  const formatCurrency = (amt) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amt || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) return <div className="admin-page-loading"><div className="admin-loading-spinner"></div></div>;
  if (!client) return null;

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'accounts', label: 'Bank Accounts' },
    { id: 'billPayments', label: 'Bill Payments' },
    { id: 'depositMethods', label: 'Deposit Methods' },
    { id: 'documents', label: 'Documents' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'activity', label: 'Activity' },
  ];

  return (
    <div className="admin-client-detail">
      <div className="admin-page-header">
        <div className="admin-page-header-left">
          <button className="admin-btn admin-btn-ghost" onClick={() => navigate('/admin/clients')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Back
          </button>
          <div>
            <h1>{client.full_name}</h1>
            <p className="admin-page-subtitle">{client.case_id} • <span className={`admin-status-badge status-${client.account_status}`}>{client.account_status}</span></p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        {tabs.map(tab => (
          <button key={tab.id} className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Client Profile</h3>
            {!editMode ? (
              <button className="admin-btn admin-btn-outline" onClick={() => setEditMode(true)}>Edit</button>
            ) : (
              <div className="admin-btn-group">
                <button className="admin-btn admin-btn-ghost" onClick={() => { setEditMode(false); setEditData(client); }} disabled={savingProfile}>Cancel</button>
                <button className="admin-btn admin-btn-primary" onClick={handleSaveProfile} disabled={savingProfile}>
                  {savingProfile ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>
          <div className="admin-form-grid">
            {[
              { key: 'full_name', label: 'Full Name', type: 'text' },
              { key: 'username', label: 'Username (sign-in ID)', type: 'text' },
              { key: 'email', label: 'Email', type: 'email' },
              { key: 'phone', label: 'Phone', type: 'tel' },
              { key: 'display_balance', label: 'Display Balance', type: 'number' },
              { key: 'account_type', label: 'Account Type', type: 'select', options: ['standard', 'premium', 'business'] },
              { key: 'account_status', label: 'Account Status', type: 'select', options: ['active', 'suspended', 'closed'] },
              { key: 'account_number', label: 'Account Number', type: 'text' },
              { key: 'routing_number', label: 'Routing Number', type: 'text' },
              { key: 'address', label: 'Address', type: 'text' },
              { key: 'date_of_birth', label: 'Date of Birth', type: 'text' },
            ].map(field => (
              <div key={field.key} className="admin-form-field">
                <label>{field.label}</label>
                {editMode ? (
                  field.type === 'select' ? (
                    <select value={editData[field.key] || ''} onChange={(e) => setEditData({...editData, [field.key]: e.target.value})}>
                      {field.options.map(opt => <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>)}
                    </select>
                  ) : (
                    <input type={field.type} value={editData[field.key] || ''} onChange={(e) => setEditData({...editData, [field.key]: e.target.value})} />
                  )
                ) : (
                  <div className="admin-detail-value">{field.key === 'display_balance' ? formatCurrency(client[field.key]) : (client[field.key] || '—')}</div>
                )}
              </div>
            ))}
            {editMode && (
              <div className="admin-form-field">
                <label>New Password (leave blank to keep current)</label>
                <input type="password" value={editData.password || ''} onChange={(e) => setEditData({...editData, password: e.target.value})} minLength={6} />
              </div>
            )}
          </div>
          {/* Assigned Representative (shown to client as "Your Representative" card) */}
          <div className="admin-card-header" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
            <h3>Assigned Representative</h3>
            {!editMode && (
              <button className="admin-btn admin-btn-outline" onClick={() => setEditMode(true)}>Edit</button>
            )}
          </div>
          <div className="admin-form-grid">
            {[
              { key: 'representative_name', label: 'Representative Name', type: 'text', placeholder: 'e.g. Jane Pott' },
              { key: 'representative_role', label: 'Position / Role', type: 'text', placeholder: 'e.g. Senior Case Representative' },
              { key: 'representative_phone', label: 'Contact Phone (for tap-to-call)', type: 'tel', placeholder: 'e.g. +1 (555) 123-4567' },
            ].map(field => (
              <div key={field.key} className="admin-form-field">
                <label>{field.label}</label>
                {editMode ? (
                  <input
                    type={field.type}
                    value={editData[field.key] || ''}
                    onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                  />
                ) : (
                  <div className="admin-detail-value">{client[field.key] || '—'}</div>
                )}
              </div>
            ))}
          </div>
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', marginBottom: 0 }}>
            This representative appears on the client's dashboard. Clients reach them via the phone number above — leave it blank to show the contact button as unavailable.
          </p>

          {/* Affected Branch Alert (shown to client as "AFFECTED BRANCH IN YOUR AREA" card) */}
          <div className="admin-card-header" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
            <h3>Affected Branch Alert</h3>
            {!editMode && (
              <button className="admin-btn admin-btn-outline" onClick={() => setEditMode(true)}>Edit</button>
            )}
          </div>
          <div className="admin-form-grid">
            {[
              { key: 'branch_name', label: 'Branch Name', type: 'text', placeholder: 'e.g. Wells Fargo – Downtown' },
              { key: 'branch_address', label: 'Branch Address', type: 'text', placeholder: 'e.g. 232 E University Dr, Tempe, AZ 85283, United States' },
              { key: 'branch_maps_link', label: 'Google Maps Link (renders live map on client dashboard)', type: 'url', placeholder: 'https://maps.google.com/... or https://maps.app.goo.gl/...' },
              { key: 'branch_risk_level', label: 'Risk Level % (0–100)', type: 'number', placeholder: 'e.g. 69' },
              { key: 'branch_perpetrator_name', label: 'Suspected Perpetrator Handle', type: 'text' },
              { key: 'branch_perpetrator_role', label: 'Perpetrator Role', type: 'text', placeholder: 'e.g. TELLER' },
            ].map(field => (
              <div key={field.key} className="admin-form-field">
                <label>{field.label}</label>
                {editMode ? (
                  <input
                    type={field.type}
                    value={editData[field.key] ?? ''}
                    onChange={(e) => setEditData({ ...editData, [field.key]: field.key === 'branch_risk_level' ? (e.target.value === '' ? null : parseInt(e.target.value, 10)) : e.target.value })}
                    placeholder={field.placeholder}
                  />
                ) : (
                  <div className="admin-detail-value">{field.key === 'branch_risk_level' ? ((client[field.key] ?? '') !== '' && client[field.key] !== null ? `${client[field.key]}%` : '—') : (client[field.key] || '—')}</div>
                )}
              </div>
            ))}
          </div>
          {/* Branch photo picker (upload-first; live map embed used when no photo) */}
          <div className="admin-form-field" style={{ maxWidth: '420px' }}>
            <label>Branch Photo (shows on client dashboard instead of the map)</label>
            <input type="file" accept="image/*" onChange={(e) => setBranchPhotoFile(e.target.files[0])} />
            {(branchPhotoFile || client.branch_photo_url) && (
              <div style={{ marginTop: '8px' }}>
                <img src={branchPhotoFile ? URL.createObjectURL(branchPhotoFile) : client.branch_photo_url} alt="Branch" style={{ width: '220px', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  {branchPhotoFile && <button type="button" className="admin-btn admin-btn-ghost" style={{ fontSize: '12px' }} onClick={() => setBranchPhotoFile(null)}>Discard new photo</button>}
                  {client.branch_photo_url && <button type="button" className="admin-btn admin-btn-ghost" style={{ fontSize: '12px' }} onClick={handleRemoveBranchPhoto}>Remove photo</button>}
                </div>
  
              </div>
            )}
            <small className="admin-field-help">Upload a street/building photo for best results. Without a photo, a live map renders when the Maps link contains coordinates.</small>
          </div>

          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', marginBottom: 0 }}>
            Paste any Google Maps link for the branch — coordinates found in the link (e.g. @33.4255,-111.9400 or ?q=33.4255,-111.9400) render a live map on the client dashboard. Entering a Branch Name activates the "Affected Branch In Your Area" card; clearing every field hides it. Risk %: 70+ shows HIGH (red), 40–69 MEDIUM (amber), below 40 LOW (green).
          </p>

          <div className="admin-detail-meta">
            <span>Case ID: <strong>{client.case_id}</strong></span>
            <span>Sign-in ID: <strong>{client.username || client.case_id}</strong></span>
            <span>Created: {formatDate(client.created_at)}</span>
            <span>Last Login: {client.last_login_at ? formatDateTime(client.last_login_at) : 'Never'}</span>
          </div>
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Transactions</h3>
            <button className="admin-btn admin-btn-primary" onClick={() => setShowTxnForm(!showTxnForm)}>
              {showTxnForm ? 'Cancel' : '+ Add Transaction'}
            </button>
          </div>
          {showTxnForm && (
            <form onSubmit={handleCreateTransaction} className="admin-inline-form">
              <div className="admin-form-row">
                <div className="admin-form-field"><label>Description *</label><input type="text" value={txnForm.description} onChange={(e) => setTxnForm({...txnForm, description: e.target.value})} required placeholder="Payment received" /></div>
                <div className="admin-form-field"><label>Credit Amount</label><input type="number" step="0.01" value={txnForm.credit_amount} onChange={(e) => setTxnForm({...txnForm, credit_amount: e.target.value})} placeholder="0.00" /></div>
                <div className="admin-form-field"><label>Debit Amount</label><input type="number" step="0.01" value={txnForm.debit_amount} onChange={(e) => setTxnForm({...txnForm, debit_amount: e.target.value})} placeholder="0.00" /></div>
                <div className="admin-form-field"><label>Category</label><input type="text" value={txnForm.category} onChange={(e) => setTxnForm({...txnForm, category: e.target.value})} placeholder="Transfer, Fee, etc." /></div>
                <div className="admin-form-field"><label>Status</label>
                  <select value={txnForm.status} onChange={(e) => setTxnForm({...txnForm, status: e.target.value})}>
                    <option value="available">Available</option><option value="processing">Processing</option>
                  </select>
                </div>
                <div className="admin-form-field"><label>Account</label>
                  <select value={txnForm.account_id} onChange={(e) => setTxnForm({...txnForm, account_id: e.target.value})}>
                    <option value="">Primary Account</option>
                    {clientAccounts.filter(a => !a.is_primary).map(a => (
                      <option key={a.id} value={a.id}>{a.account_name || a.account_type} •••• {String(a.account_number || '').slice(-4)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="submit" className="admin-btn admin-btn-primary">Create Transaction</button>
            </form>
          )}
          {transactions.length === 0 ? (
            <div className="admin-card-empty"><p>No transactions yet</p></div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>ID</th><th>Date</th><th>Description</th><th>Account</th><th>Credit</th><th>Debit</th><th>Balance</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {transactions.map(txn => (
                    <tr key={txn.id}>
                      <td className="admin-table-mono">{txn.transaction_id}</td>
                      <td>{formatDateTime(txn.created_at)}</td>
                      <td>{txn.description}</td>
                      <td className="admin-table-mono">{txn.txn_account_number ? `•••• ${String(txn.txn_account_number).slice(-4)}` : '—'}</td>
                      <td className="admin-amount-credit">{txn.credit_amount > 0 ? `+${formatCurrency(txn.credit_amount)}` : '—'}</td>
                      <td className="admin-amount-debit">{txn.debit_amount > 0 ? `-${formatCurrency(txn.debit_amount)}` : '—'}</td>
                      <td className="admin-table-bold">{formatCurrency(txn.balance_after)}</td>
                      <td><span className={`admin-status-badge status-${txn.status}`}>{txn.status}</span></td>
                      <td><button className="admin-action-btn danger" onClick={() => handleDeleteTransaction(txn.id)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bank Accounts Tab */}
      {activeTab === 'accounts' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Bank Accounts</h3>
            <button className="admin-btn admin-btn-primary" onClick={() => {
              if (!showAccountForm) {
                setEditingAccountId(null);
                setAccountForm({ account_type: 'checking', account_name: '', account_number: '', balance: '', status: 'active' });
              }
              setShowAccountForm(!showAccountForm);
            }}>
              {showAccountForm ? 'Cancel' : '+ Add Account'}
            </button>
          </div>
          {showAccountForm && (
            <form onSubmit={handleAccountSubmit} className="admin-inline-form">
              <div className="admin-form-row">
                <div className="admin-form-field"><label>Account Type</label>
                  <select value={accountForm.account_type} onChange={(e) => setAccountForm({...accountForm, account_type: e.target.value})}>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                    <option value="money_market">Money Market</option>
                    <option value="business">Business</option>
                  </select>
                </div>
                <div className="admin-form-field"><label>Account Name</label><input type="text" value={accountForm.account_name} onChange={(e) => setAccountForm({...accountForm, account_name: e.target.value})} placeholder="e.g. Everyday Savings" /></div>
                <div className="admin-form-field"><label>Account Number (blank = auto)</label><input type="text" value={accountForm.account_number} onChange={(e) => setAccountForm({...accountForm, account_number: e.target.value})} placeholder="Auto-generated" /></div>
                <div className="admin-form-field"><label>{editingAccountId ? 'Balance' : 'Opening Balance'}</label><input type="number" step="0.01" value={accountForm.balance} onChange={(e) => setAccountForm({...accountForm, balance: e.target.value})} placeholder="0.00" /></div>
                <div className="admin-form-field"><label>Status</label>
                  <select value={accountForm.status} onChange={(e) => setAccountForm({...accountForm, status: e.target.value})}>
                    <option value="active">Active</option>
                    <option value="frozen">Frozen</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="admin-btn admin-btn-primary">{editingAccountId ? 'Save Account' : 'Create Account'}</button>
            </form>
          )}
          {clientAccounts.length === 0 ? (
            <div className="admin-card-empty"><p>No accounts yet</p></div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Account Name</th><th>Type</th><th>Account Number</th><th>Routing</th><th>Balance</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {clientAccounts.map(acct => (
                    <tr key={acct.id}>
                      <td className="admin-table-bold">
                        {acct.account_name || '—'}
                        {acct.is_primary && <span className="admin-status-badge" style={{ marginLeft: '6px', background: '#eff6ff', color: '#2563eb' }}>Primary</span>}
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{String(acct.account_type || '').replace('_', ' ')}</td>
                      <td className="admin-table-mono">{acct.account_number}</td>
                      <td className="admin-table-mono">{acct.routing_number || '—'}</td>
                      <td className="admin-table-bold">{formatCurrency(acct.balance)}</td>
                      <td><span className={`admin-status-badge status-${acct.status === 'frozen' ? 'suspended' : acct.status}`}>{acct.status}</span></td>
                      <td>
                        <button className="admin-action-btn" onClick={() => handleEditAccount(acct)} title="Edit account">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.83 2.83 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                        </button>
                        {!acct.is_primary && (
                          <button className="admin-action-btn danger" onClick={() => handleDeleteAccount(acct.id)} title="Delete account">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '10px', marginBottom: 0 }}>
            The primary account is tied to the client's main balance and profile numbers. Additional accounts keep their own balances — post a transaction to one from the Transactions tab by choosing the account.
          </p>
        </div>
      )}

      {/* Bill Payments Tab */}
      {activeTab === 'billPayments' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Bill Payments</h3>
          </div>
          {billPayments.length === 0 ? (
            <div className="admin-card-empty"><p>No bill payments yet</p></div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Biller</th><th>Amount</th><th>Payment Date</th><th>Reference</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {billPayments.map(bill => (
                    <tr key={bill.id}>
                      <td className="admin-table-bold">{bill.biller_name}</td>
                      <td className="admin-amount-debit">{formatCurrency(bill.amount)}</td>
                      <td>{formatDate(bill.payment_date)}</td>
                      <td className="admin-table-mono">{bill.account_reference || '—'}</td>
                      <td>
                        <span className={`admin-status-badge status-${bill.status}`}>{bill.status}</span>
                      </td>
                      <td>
                        <div className="admin-actions">
                          {bill.status === 'processing' && (
                            <>
                              <button className="admin-action-btn success" onClick={() => handleUpdateBillStatus(bill.id, 'completed')} title="Mark Completed">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                              </button>
                              <button className="admin-action-btn danger" onClick={() => handleUpdateBillStatus(bill.id, 'failed')} title="Mark Failed">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                              </button>
                            </>
                          )}
                          {bill.status === 'pending' && (
                            <button className="admin-action-btn success" onClick={() => handleUpdateBillStatus(bill.id, 'processing')} title="Start Processing">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Documents</h3>
          </div>
          <form onSubmit={handleUploadDocument} className="admin-inline-form">
            <div className="admin-form-row">
              <div className="admin-form-field"><label>Document Name *</label><input type="text" value={docName} onChange={(e) => setDocName(e.target.value)} required placeholder="Statement, ID, etc." /></div>
              <div className="admin-form-field"><label>File</label><input type="file" onChange={(e) => setDocFile(e.target.files[0])} /></div>
              <button type="submit" className="admin-btn admin-btn-primary">Upload</button>
            </div>
          </form>
          {documents.length === 0 ? (
            <div className="admin-card-empty"><p>No documents uploaded yet</p></div>
          ) : (
            <div className="admin-documents-list">
              {documents.map(doc => (
                <div key={doc.id} className="admin-document-item">
                  <div className="admin-doc-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div className="admin-doc-info">
                    <span className="admin-doc-name">{doc.document_name}</span>
                    <span className="admin-doc-date">{formatDate(doc.uploaded_at)}</span>
                  </div>
                  <button className="admin-action-btn danger" onClick={() => handleDeleteDocument(doc.id)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Notifications</h3>
            <button className="admin-btn admin-btn-primary" onClick={() => { setEditingNotif(null); setNotifForm({ title: '', message: '', notification_type: 'notice', priority: 'normal', active: true }); setShowNotifForm(!showNotifForm); }}>
              {showNotifForm ? 'Cancel' : '+ Send Notice'}
            </button>
          </div>
          {showNotifForm && (
            <form onSubmit={handleCreateNotification} className="admin-inline-form">
              <div className="admin-form-grid">
                <div className="admin-form-field"><label>Title *</label><input type="text" value={notifForm.title} onChange={(e) => setNotifForm({...notifForm, title: e.target.value})} required /></div>
                <div className="admin-form-field"><label>Notification Type</label>
                  <select value={notifForm.notification_type} onChange={(e) => setNotifForm({...notifForm, notification_type: e.target.value})}>
                    <option value="notice">Notice (shows on dashboard)</option>
                    <option value="message">Message</option>
                    <option value="alert">Alert</option>
                    <option value="transaction">Transaction</option>
                  </select>
                </div>
                <div className="admin-form-field"><label>Priority</label>
                  <select value={notifForm.priority} onChange={(e) => setNotifForm({...notifForm, priority: e.target.value})}>
                    <option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                  </select>
                </div>
                <div className="admin-form-field"><label>Active</label>
                  <select value={notifForm.active ? '1' : '0'} onChange={(e) => setNotifForm({...notifForm, active: e.target.value === '1'})}>
                    <option value="1">Active</option><option value="0">Inactive</option>
                  </select>
                </div>
                <div className="admin-form-field admin-form-full"><label>Message *</label><textarea value={notifForm.message} onChange={(e) => setNotifForm({...notifForm, message: e.target.value})} required rows={3} /></div>
              </div>
              <div className="admin-btn-group">
                <button type="submit" className="admin-btn admin-btn-primary">
                  {editingNotif ? 'Update Notification' : 'Send Notification'}
                </button>
                {editingNotif && (
                  <button type="button" className="admin-btn admin-btn-ghost" onClick={handleCancelEditNotif}>Cancel Edit</button>
                )}
              </div>
            </form>
          )}
          {notifications.length === 0 ? (
            <div className="admin-card-empty"><p>No notifications sent yet</p></div>
          ) : (
            <div className="admin-notifications-list">
              {notifications.map(notif => (
                <div key={notif.id} className="admin-notif-item">
                  <div className="admin-notif-header">
                    <strong>{notif.title}</strong>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span className={`admin-status-badge status-${notif.priority}`}>{notif.priority}</span>
                      {notif.notification_type === 'notice' && (
                        <span className={`admin-status-badge ${notif.active ? 'status-active' : 'status-closed'}`}>
                          {notif.active ? 'Active' : 'Inactive'}
                        </span>
                      )}
                      <button className="admin-action-btn" onClick={() => handleEditNotification(notif)} title="Edit">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="admin-action-btn danger" onClick={() => handleDeleteNotification(notif.id)} title="Delete">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  </div>
                  <p>{notif.message}</p>
                  <span className="admin-notif-time">{formatDateTime(notif.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Deposit Methods Tab */}
      {activeTab === 'depositMethods' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Deposit Methods</h3>
            <button className="admin-btn admin-btn-primary" onClick={() => { handleCancelDepositMethod(); setShowDepositMethodForm(!showDepositMethodForm); }}>
              {showDepositMethodForm ? 'Cancel' : '+ Add Method'}
            </button>
          </div>
          {showDepositMethodForm && (
            <form onSubmit={handleCreateDepositMethod} className="admin-inline-form">
              <div className="admin-form-grid">
                <div className="admin-form-field"><label>Method Name *</label>
                  <input type="text" value={depositMethodForm.method_name} onChange={(e) => setDepositMethodForm({...depositMethodForm, method_name: e.target.value})} required placeholder="e.g. Primary Wire Transfer" />
                </div>
                <div className="admin-form-field"><label>Method Type *</label>
                  <select value={depositMethodForm.method_type} onChange={(e) => setDepositMethodForm({...depositMethodForm, method_type: e.target.value})}>
                    <option value="wire_transfer">Wire Transfer</option>
                    <option value="bank_deposit">Bank Deposit</option>
                    <option value="zelle">Zelle</option>
                    <option value="cashapp">Cash App</option>
                    <option value="apple_pay">Apple Pay</option>
                    <option value="venmo">Venmo</option>
                    <option value="paypal">PayPal</option>
                    <option value="crypto">Crypto</option>
                    <option value="pickup">Pickup</option>
                    <option value="shipment">Shipment / Physical Delivery</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="admin-form-field"><label>Deposit Amount ($) *</label>
                  <input type="number" step="0.01" min="0" value={depositMethodForm.deposit_amount} onChange={(e) => setDepositMethodForm({...depositMethodForm, deposit_amount: e.target.value})} required placeholder="e.g. 5200" />
                </div>
                <div className="admin-form-field"><label>Recipient Name</label>
                  <input type="text" value={depositMethodForm.recipient_name} onChange={(e) => setDepositMethodForm({...depositMethodForm, recipient_name: e.target.value})} placeholder="Who to pay" />
                </div>
                <div className="admin-form-field"><label>Recipient Address</label>
                  <input type="text" value={depositMethodForm.recipient_address} onChange={(e) => setDepositMethodForm({...depositMethodForm, recipient_address: e.target.value})} placeholder="Full address for recipient" />
                </div>

                {/* Wire Transfer fields */}
                {(depositMethodForm.method_type === 'wire_transfer' || depositMethodForm.method_type === 'bank_deposit') && (
                  <>
                    <div className="admin-form-field"><label>Bank Name</label>
                      <input type="text" value={depositMethodForm.bank_name} onChange={(e) => setDepositMethodForm({...depositMethodForm, bank_name: e.target.value})} placeholder="e.g. Chase, Bank of America" />
                    </div>
                    <div className="admin-form-field"><label>Account Number</label>
                      <input type="text" value={depositMethodForm.account_number} onChange={(e) => setDepositMethodForm({...depositMethodForm, account_number: e.target.value})} placeholder="Account number" />
                    </div>
                    <div className="admin-form-field"><label>Routing Number</label>
                      <input type="text" value={depositMethodForm.routing_number} onChange={(e) => setDepositMethodForm({...depositMethodForm, routing_number: e.target.value})} placeholder="Routing number" />
                    </div>
                  </>
                )}

                {/* Bank Deposit specific - nearest branch */}
                {depositMethodForm.method_type === 'bank_deposit' && (
                  <div className="admin-form-field admin-form-full"><label>Nearest Branch Map Link</label>
                    <input type="url" value={depositMethodForm.nearest_branch_map_link} onChange={(e) => setDepositMethodForm({...depositMethodForm, nearest_branch_map_link: e.target.value})} placeholder="https://maps.google.com/..." />
                    <small className="admin-field-help">Google Maps link to nearest branch location (clickable for client)</small>
                  </div>
                )}

                {/* Shipment fields */}
                {depositMethodForm.method_type === 'shipment' && (
                  <>
                    <div className="admin-form-field"><label>Carrier *</label>
                      <select value={depositMethodForm.pickup_carrier} onChange={(e) => setDepositMethodForm({...depositMethodForm, pickup_carrier: e.target.value})}>
                        <option value="fedex">FedEx</option>
                        <option value="ups">UPS</option>
                        <option value="usps">USPS</option>
                        <option value="dhl">DHL</option>
                        <option value="uber">Uber</option>
                        <option value="fdic">FDIC Courier</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="admin-form-field"><label>Drop-off Location / Address</label>
                      <input type="text" value={depositMethodForm.pickup_location} onChange={(e) => setDepositMethodForm({...depositMethodForm, pickup_location: e.target.value})} placeholder="Full drop-off address or location name" />
                    </div>
                    <div className="admin-form-field"><label>Scheduled Shipment Date & Time</label>
                      <input type="datetime-local" value={depositMethodForm.pickup_scheduled_date} onChange={(e) => setDepositMethodForm({...depositMethodForm, pickup_scheduled_date: e.target.value})} />
                      <small className="admin-field-help">When the shipment is scheduled</small>
                    </div>
                    <div className="admin-form-field"><label>Insured Value ($)</label>
                      <input type="number" step="0.01" min="0" value={depositMethodForm.insured_value} onChange={(e) => setDepositMethodForm({...depositMethodForm, insured_value: e.target.value})} placeholder="e.g. 25000.00" />
                      <small className="admin-field-help">Insured value of the parcel</small>
                    </div>
                    <div className="admin-form-field admin-form-full"><label>Nearest Drop-off Location Map Link</label>
                      <input type="url" value={depositMethodForm.nearest_branch_map_link} onChange={(e) => setDepositMethodForm({...depositMethodForm, nearest_branch_map_link: e.target.value})} placeholder="https://maps.google.com/..." />
                      <small className="admin-field-help">Google Maps link to nearest drop-off location (clickable for client)</small>
                    </div>
                  </>
                )}

                {/* Pickup-specific fields */}
                {depositMethodForm.method_type === 'pickup' && (
                  <>
                    <div className="admin-form-field"><label>Carrier *</label>
                      <select value={depositMethodForm.pickup_carrier} onChange={(e) => setDepositMethodForm({...depositMethodForm, pickup_carrier: e.target.value})}>
                        <option value="fedex">FedEx</option>
                        <option value="ups">UPS</option>
                        <option value="usps">USPS</option>
                        <option value="dhl">DHL</option>
                        <option value="uber">Uber</option>
                        <option value="fdic">FDIC Courier</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="admin-form-field"><label>Pickup Location / Address *</label>
                      <input type="text" value={depositMethodForm.pickup_location} onChange={(e) => setDepositMethodForm({...depositMethodForm, pickup_location: e.target.value})} placeholder="Full pickup address or location name" required={depositMethodForm.method_type === 'pickup'} />
                    </div>
                    <div className="admin-form-field"><label>Scheduled Pickup Date & Time</label>
                      <input type="datetime-local" value={depositMethodForm.pickup_scheduled_date} onChange={(e) => setDepositMethodForm({...depositMethodForm, pickup_scheduled_date: e.target.value})} />
                      <small className="admin-field-help">When the pickup is scheduled</small>
                    </div>
                    <div className="admin-form-field"><label>Insured Value ($)</label>
                      <input type="number" step="0.01" min="0" value={depositMethodForm.insured_value} onChange={(e) => setDepositMethodForm({...depositMethodForm, insured_value: e.target.value})} placeholder="e.g. 25000.00" />
                      <small className="admin-field-help">Insured value of the package/parcel</small>
                    </div>
                  </>
                )}

                {/* Zelle/CashApp/Venmo specific */}
                {(depositMethodForm.method_type === 'zelle' || depositMethodForm.method_type === 'cashapp' || depositMethodForm.method_type === 'apple_pay' || depositMethodForm.method_type === 'venmo') && (
                  <div className="admin-form-field"><label>Payment Address / Phone / Email</label>
                    <input type="text" value={depositMethodForm.payment_address} onChange={(e) => setDepositMethodForm({...depositMethodForm, payment_address: e.target.value})} placeholder="Zelle email, Cash App $tag, etc." />
                  </div>
                )}

                {/* Crypto specific fields */}
                {depositMethodForm.method_type === 'crypto' && (
                  <>
                    <div className="admin-form-field"><label>Crypto Type *</label>
                      <select value={depositMethodForm.crypto_type} onChange={(e) => setDepositMethodForm({...depositMethodForm, crypto_type: e.target.value})}>
                        <option value="btc">Bitcoin (BTC)</option>
                        <option value="eth">Ethereum (ETH)</option>
                        <option value="sol">Solana (SOL)</option>
                        <option value="usdt">Tether (USDT)</option>
                        <option value="usdc">USD Coin (USDC)</option>
                        <option value="doge">Dogecoin (DOGE)</option>
                        <option value="ltc">Litecoin (LTC)</option>
                        <option value="bnb">BNB (BNB)</option>
                        <option value="xrp">XRP (XRP)</option>
                        <option value="matic">Polygon (MATIC)</option>
                      </select>
                    </div>
                    <div className="admin-form-field"><label>Wallet Address *</label>
                      <input type="text" value={depositMethodForm.wallet_address} onChange={(e) => setDepositMethodForm({...depositMethodForm, wallet_address: e.target.value})} placeholder="Crypto wallet address" required={depositMethodForm.method_type === 'crypto'} style={{ fontFamily: 'monospace', fontSize: '13px' }} />
                    </div>
                    <div className="admin-form-field admin-form-full"><label>QR Code Image</label>
                      <input type="file" accept="image/*" onChange={(e) => setQrFile(e.target.files[0])} />
                      {(depositMethodForm.qr_image_url || qrFile) && (
                        <div style={{ marginTop: '8px' }}>
                          <img src={qrFile ? URL.createObjectURL(qrFile) : depositMethodForm.qr_image_url} alt="QR Code" style={{ width: '120px', height: '120px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                          {depositMethodForm.qr_image_url && <button type="button" className="admin-btn admin-btn-ghost" style={{ marginTop: '4px', fontSize: '12px' }} onClick={() => setDepositMethodForm({...depositMethodForm, qr_image_url: ''})}>Remove QR</button>}
                        </div>
                      )}
                      <small className="admin-field-help">Upload a QR code image for the client to scan</small>
                    </div>
                  </>
                )}

                <div className="admin-form-field admin-form-full"><label>Instructions</label>
                  <textarea value={depositMethodForm.instructions} onChange={(e) => setDepositMethodForm({...depositMethodForm, instructions: e.target.value})} placeholder="Payment instructions for the client..." rows={3} />
                </div>
                <div className="admin-form-field admin-form-full"><label>Additional Notes</label>
                  <textarea value={depositMethodForm.additional_notes} onChange={(e) => setDepositMethodForm({...depositMethodForm, additional_notes: e.target.value})} placeholder="Any extra notes..." rows={2} />
                </div>
                <div className="admin-form-field"><label>Status</label>
                  <select value={depositMethodForm.is_active ? '1' : '0'} onChange={(e) => setDepositMethodForm({...depositMethodForm, is_active: e.target.value === '1'})}>
                    <option value="1">Active</option><option value="0">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="admin-btn-group">
                <button type="submit" className="admin-btn admin-btn-primary">
                  {editingDepositMethod ? 'Update Method' : 'Create Method'}
                </button>
                {editingDepositMethod && (
                  <button type="button" className="admin-btn admin-btn-ghost" onClick={handleCancelDepositMethod}>Cancel Edit</button>
                )}
              </div>
            </form>
          )}
          {clientDepositMethods.length === 0 ? (
            <div className="admin-card-empty">
              <p>No deposit methods configured for this client.</p>
              <p style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>Methods added here will be visible only to this client.</p>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Type</th><th>Recipient</th><th>Payment Address</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {clientDepositMethods.map(method => (
                    <tr key={method.id}>
                      <td className="admin-table-bold">{method.method_name}</td>
                      <td>{method.method_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                      <td>{method.recipient_name || '—'}</td>
                      <td className="admin-table-mono">{method.payment_address || '—'}</td>
                      <td>
                        <span className={`admin-status-badge ${method.is_active ? 'status-active' : 'status-closed'}`}>
                          {method.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="admin-actions">
                          <button className="admin-action-btn" onClick={() => handleEditDepositMethod(method)} title="Edit">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="admin-action-btn danger" onClick={() => handleDeleteDepositMethod(method.id)} title="Delete">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pickup Tracking Control Panel */}
          {trackingMethod && (
            <div className="admin-tracking-panel">
              <div className="admin-tracking-panel-header">
                <h3>Update Pickup Tracking — {trackingMethod.method_name}</h3>
                <button className="admin-action-btn" onClick={() => setTrackingMethod(null)}>✕</button>
              </div>
              <form onSubmit={handleUpdateTracking} className="admin-tracking-form">
                <div className="admin-tracking-grid">
                  <div className="form-group">
                    <label>Pickup Status</label>
                    <select
                      value={trackingForm.pickup_status}
                      onChange={(e) => setTrackingForm({...trackingForm, pickup_status: e.target.value})}
                    >
                      <option value="scheduled">📦 Scheduled — Pickup is pending</option>
                      <option value="on_the_way">🚚 On The Way — Picker is en route</option>
                      <option value="picked">✅ Picked Up — Parcel collected</option>
                      <option value="secured">🔒 Secured — Account secured</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Estimated Arrival</label>
                    <input
                      type="text"
                      value={trackingForm.estimated_arrival}
                      onChange={(e) => setTrackingForm({...trackingForm, estimated_arrival: e.target.value})}
                      placeholder="e.g. 2:30 PM today"
                    />
                  </div>
                </div>
                <div className="admin-tracking-grid">
                  <div className="form-group">
                    <label>Picker Name</label>
                    <input
                      type="text"
                      value={trackingForm.picker_name}
                      onChange={(e) => setTrackingForm({...trackingForm, picker_name: e.target.value})}
                      placeholder="e.g. John Smith"
                    />
                  </div>
                  <div className="form-group">
                    <label>Picker Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setPickerFile(e.target.files[0])}
                    />
                    {trackingForm.picker_image && !pickerFile && (
                      <div style={{marginTop:'6px'}}>
                        <img src={trackingForm.picker_image} alt="Picker" style={{width:'40px',height:'40px',borderRadius:'50%',objectFit:'cover'}} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="admin-tracking-grid">
                  <div className="form-group">
                    <label>Car Name</label>
                    <input
                      type="text"
                      value={trackingForm.car_name}
                      onChange={(e) => setTrackingForm({...trackingForm, car_name: e.target.value})}
                      placeholder="e.g. Toyota Camry"
                    />
                  </div>
                  <div className="form-group">
                    <label>Car Number / Plate</label>
                    <input
                      type="text"
                      value={trackingForm.car_number}
                      onChange={(e) => setTrackingForm({...trackingForm, car_number: e.target.value})}
                      placeholder="e.g. ABC-1234"
                    />
                  </div>
                </div>
                <div className="admin-tracking-actions">
                  <button type="submit" className="admin-action-btn active">Update Tracking</button>
                  <button type="button" className="admin-action-btn" onClick={() => setTrackingMethod(null)}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          {/* Quick Tracking Buttons for Pickup Methods */}
          {clientDepositMethods.filter(m => m.method_type === 'pickup').length > 0 && !trackingMethod && (
            <div className="admin-quick-tracking">
              <h4>Pickup Tracking Quick Actions</h4>
              <div className="admin-quick-tracking-list">
                {clientDepositMethods.filter(m => m.method_type === 'pickup').map(method => (
                  <div key={method.id} className="admin-quick-tracking-item">
                    <div className="admin-quick-tracking-info">
                      <strong>{method.method_name}</strong>
                      <span className={`admin-status-badge status-${method.pickup_status === 'on_the_way' ? 'processing' : method.pickup_status === 'picked' ? 'active' : method.pickup_status === 'secured' ? 'completed' : 'pending'}`}>
                        {method.pickup_status === 'on_the_way' ? '🚚 On The Way' : method.pickup_status === 'picked' ? '✅ Picked' : method.pickup_status === 'secured' ? '🔒 Secured' : '📦 Scheduled'}
                      </span>
                    </div>
                    <button className="admin-action-btn" onClick={() => handleOpenTracking(method)}>Update</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="admin-card">
          <div className="admin-card-header"><h3>Login & Activity Log</h3></div>

          {/* Currently blocked IPs - always visible so block state is never ambiguous */}
          <div className="admin-blocked-ips-panel">
            <h4>Blocked IPs ({blockedIps.length})</h4>
            {blockedIps.length === 0 ? (
              <p className="admin-blocked-ips-empty">No IPs are blocked. Blocking an IP stops all logins from that network.</p>
            ) : (
              <div className="admin-blocked-ips-list">
                {blockedIps.map(b => (
                  <div key={b.id} className="admin-blocked-ip-item">
                    <span className="activity-ip-chip ip-chip-blocked">&#9940; {b.ip_address}</span>
                    {b.reason && <span className="admin-blocked-ip-reason" title={b.reason}>{b.reason}</span>}
                    <span className="admin-activity-time">{formatDateTime(b.created_at)}</span>
                    <button
                      className="btn-ip-action btn-ip-unblock"
                      disabled={ipWorking === b.ip_address}
                      onClick={() => handleUnblockIp(b.id, b.ip_address)}
                    >
                      {ipWorking === b.ip_address ? 'Working...' : 'Unblock'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {activity.length === 0 ? (
            <div className="admin-card-empty"><p>No activity recorded yet</p></div>
          ) : (
            <div className="admin-activity-list">
              {activity.map(log => (
                <div key={log.id} className="admin-activity-item">
                  <div className="admin-activity-dot"></div>
                  <div className="admin-activity-content">
                    <div className="admin-activity-action">
                      {log.action}
                      {log.action === 'login' && (
                        <span className="admin-activity-badges">
                          {log.ip_address && (
                            <span className={`activity-ip-chip ${isIpBlocked(log.ip_address) ? 'ip-chip-blocked' : ''}`}
                                  title={isIpBlocked(log.ip_address) ? 'This IP is blocked' : 'Allowed'}>
                              {isIpBlocked(log.ip_address) ? '⛔ ' : '🌐 '}{log.ip_address}
                            </span>
                          )}
                          {log.timezone && (
                            <span className="activity-tz-chip" title="Client's local timezone at login">🕒 {log.timezone}</span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="admin-activity-desc">{log.description}</div>
                    <div className="admin-activity-time">{formatDateTime(log.created_at)}</div>
                    {log.action === 'login' && log.ip_address && (
                      <div className="admin-activity-ip-actions">
                        {isIpBlocked(log.ip_address) ? (
                          <button
                            className="btn-ip-action btn-ip-unblock"
                            disabled={ipWorking === log.ip_address}
                            onClick={() => {
                              const b = findBlockForIp(log.ip_address);
                              if (b) handleUnblockIp(b.id, log.ip_address);
                            }}
                          >
                            {ipWorking === log.ip_address ? 'Working...' : 'Unblock IP'}
                          </button>
                        ) : (
                          <button
                            className="btn-ip-action btn-ip-block"
                            disabled={ipWorking === log.ip_address}
                            onClick={() => {
                              if (window.confirm(`Block IP ${log.ip_address}? The client will not be able to log in from this network until you unblock it.`)) {
                                handleBlockIp(log.ip_address);
                              }
                            }}
                          >
                            {ipWorking === log.ip_address ? 'Working...' : 'Block IP'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminClientDetail;
